/**
 * Shared chat behavior for the /chat page and the rail.
 *
 * Anything that changes what the agent receives or what the user can do
 * belongs here. Surfaces import this module; neither redefines it.
 * Spacing and type scale may still differ by width.
 *
 * Layout exception: the 2×2 task-card chrome (icons, category labels,
 * greeting) is page-only — a 280–420 px rail cannot hold four cards.
 * Off-demo the rail offers a single starter chip from this list
 * (`RAIL_EMPTY_PROMPT`). Demo packs may override the stacked list via
 * `window.__PROPHET_DEMO__.sidebarPrompts`.
 */

export type ChatComposerMode = 'ask' | 'agent'

/** Wire value the orchestrator route accepts for a composer mode. */
export type ChatWireMode = 'full' | 'query_agent_only'

export const DEFAULT_COMPOSER_MODE: ChatComposerMode = 'ask'
export const CHAT_SHARE_ALLOWED_ACCESS_LEVELS = ['viewer'] as const
export const CHAT_SHARE_SCOPE_DESCRIPTION =
  'Shared chats are view-only. Viewers can start a new chat from this history.'

export function composerModeToWireMode(mode: ChatComposerMode): ChatWireMode {
  return mode === 'ask' ? 'query_agent_only' : 'full'
}

export type ChatTurnActionId = 'copy'

export const CHAT_TURN_ACTIONS: readonly ChatTurnActionId[] = ['copy']

/**
 * Sent by the Continue working button after the user stops a turn.
 *
 * Stopping is not a discard: the server finalizes the run as `aborted`
 * with its partial plan and step results, and hands that transcript to
 * the next dispatch, so the agent resumes rather than starting over.
 */
export const CONTINUE_STOPPED_RUN_PROMPT = 'Continue working from where you left off.'

export type ChatTaskKind = 'diagnose' | 'alarms' | 'history' | 'build'

export interface ChatTaskPrompt {
  id: ChatTaskKind
  label: string
  prompt: string
  /** Ask cards stay in Ask; Build is the only Agent-directed card. */
  mode: ChatComposerMode
}

/**
 * Generic welcome cards. No plant entity names — these must still
 * produce a useful answer when nothing has been ingested yet.
 */
export const CHAT_TASK_PROMPTS: ChatTaskPrompt[] = [
  { id: 'diagnose', label: 'Diagnose', prompt: "What's going wrong right now?", mode: 'ask' },
  { id: 'alarms', label: 'Alarms', prompt: "What's alarming right now?", mode: 'ask' },
  { id: 'history', label: 'History', prompt: 'What do we know about this plant?', mode: 'ask' },
  { id: 'build', label: 'Build', prompt: 'Make me an OEE dashboard', mode: 'agent' }
]

/** The one starter the rail shows when it is too narrow for the 2×2 cards. */
export const RAIL_EMPTY_PROMPT = "What's alarming right now?"

export function chatPromptStrings(
  prompts: readonly ChatTaskPrompt[] = CHAT_TASK_PROMPTS
): string[] {
  return prompts.map((card) => card.prompt)
}

/**
 * Fallback chips when the Prophet demo bridge hasn't supplied a pack-specific
 * list. Kept keyword-rich so typed variants route to the right canned
 * transcript; packs override this per industry via the bridge.
 */
export const DEMO_CHAT_PROMPTS = [
  'Why is Line 2 OEE down this week?',
  "What's driving the downtime on Line 2?",
  'Build a dashboard for Plant 07 with OEE, open incidents, and tag health.',
  'Create a custom agent using the RCA template for Filler-02 on Line 2.',
  'Create a custom agent using the PM template for Air Compressor 3.',
  'Open a ServiceNow ticket for the Filler-02 issue on Line 2.',
  'The sanitizer pump on CIP Skid A was replaced last Tuesday - the new unit is a Grundfos CRN 5.'
]

export function resolveChatEmptyPrompts(isDemo: boolean): string[] {
  if (!isDemo) return [RAIL_EMPTY_PROMPT]
  const bridge = (
    window as unknown as {
      __PROPHET_DEMO__?: { sidebarPrompts?: string[] }
    }
  ).__PROPHET_DEMO__
  return bridge?.sidebarPrompts?.length ? bridge.sidebarPrompts : DEMO_CHAT_PROMPTS
}
